import {
  membershipApi,
  type companyagreementApi,
  type documentAiApi,
  type mealVouchersApi,
  type portalConfigurationApi,
  type PrivacyDocument,
  type Relationship,
  type UserFamilyMember,
} from 'app/src/services/welfareportal'
import type { OrderDetails } from 'app/src/services/welfareportal/shopping-api'
import type atlas from 'azure-maps-control'
import { AutoCompleteProps } from 'primereact/autocomplete'
import type { CalendarProps } from 'primereact/calendar'
import type { DropdownProps } from 'primereact/dropdown'
import type { InputNumberProps } from 'primereact/inputnumber'
import type { InputTextProps } from 'primereact/inputtext'
import type { CSSProperties } from 'react'

export type ErrorModel = {
  /**
   * Error type
   */
  type: string
  /**
   * HTTP status
   */
  status: number
  /**
   * Error message
   */
  title: string
  /**
   * Unique return code
   */
  rc?: number
}

export type Paging = {
  /**
   * @format int32
   */
  offset: number
  /**
   * @format int32
   */
  limit: number
  /**
   * @format int32
   */
  total: number
}

export type PaginatedResponse<T> = {
  paging: Paging
  items?: T[]
}

export type HomeI18n = {
  AppBar: {
    AltLogoImage: string
    Logout: string
    Landing: string
    Catalog: string
    Discounts: string
    ContactUs: string
    DemoSettings: string
    ContactUsSubTitle: string
    UserInfo: string
    UserAccount: string
    HowToUsePlatform: string
    GotoCatalogSearch: string
    MealVouchers: string
  }
  Global: {
    Yes: string
    No: string
    Hello: string
    Minutes: string
    Hours: string
    Days: string
    Paginator: {
      LoadMoreLabel: string
      EmptyMessage: string
    }
    Loading: {
      Message: string
    }
  }
}

export type ErrorsI18n = {
  UnrecoverableErrorMessage: string
  DownloadFileErrorMessage: string
  LogoutErrorMessage: string
  HomeBreadCrumb: string
  UserForbidden: string
  UserForbiddenTitle: string
  UserForbiddenMsgPrefix: string
  UserForbiddenMsgPostfix: string
  SessionExpiredMsg: string
  SessionExpiredBtnLabel: string
  SessionExpiredSuggestion: string
}

export type LandingI18n = {
  Landing: {
    BannerTitle: string
    BannerDescription: string
    TailBrand: string
    PoweredBy: string
    ContactUs: {
      Title: string
      CardTitle: string
      CardSubtitle: string
      CardButtonLabel: string
    }
    HomeHeader: {
      Title: string
      Text: string
    }
    Discount: {
      Title: string
      Text: string
    }
    GoToCatalog: {
      ButtonLabel: string
    }
    BackToCatalog: {
      ButtonLabel: string
    }
    BackToDiscount: {
      ButtonLabel: string
    }
    CheckCatalog: {
      ButtonLabel: string
    }
    NewsFeed: {
      Title: string
      ReadMore: string
      OpenNewsList: string
    }
    PartnerCarousel: {
      Title: string
    }
    Join: {
      Title: string
      Text: string
      ButtonLabel: string
    }
    UserSidePanel: {
      Email: string
      Iban: string
      Familymembers: string
      ButtonLabelUserData: string
      ButtonLabelExit: string
    }
    CampaignSelectionDialog: {
      Title: string
      CancelButtonLabel: string
      ConfirmButtonLabel: string
      from: string
      to: string
      InformativeText: string
    }
    TimerAndBalance: {
      [key in CampaignPhase]: string
    }
    Terms: {
      Title: string
    }
    BalanceWidget: {
      AvailableBalance: string
      ShowBalance: string
      HideBalance: string
      GoToUserArea: string
    }
    BalanceReminderDialog: {
      Title: string
      ConfirmButtonLabel: string
      ID_1: string
      ID_2: string
      ID_3: string
      ID_4: string
      ID_5: string
      ID_6: string
      ID_7: string
    }
  }
}

export type MealVouchersI18n = {
  MealVouchers: {
    LoginError: {
      title: string
      message: string
    }
    MealVouchersWallet: {
      title: string
      paymentButton: string
      updatedAtLabel: string
      activeLabel: string
      inactiveLabel: string
      yourTicketsLabel: string
      expiryDateLabel: string
      paymentButtonLabel: string
      favouriteShops: string
      seeAllFavourites: string
      seeAllVouchers: string
      transactionsListTitle: string
      backButtonLabel: string
      vouchersLabel: string
    }
    SelectShopStep: {
      title: string
      mapView: string
      listView: string
      paymentButton: string
      showDetail: string
      shopOpen: string
      shopClosed: string
      searchShopLabel: string
      positionLabel: string
      locationErrorTitle: string
      locationErrorMessage: string
      positionDialogTitle: string
      positionDialogDescription: string
      newPositionLabel: string
      selectFromMapButton: string
      positionDialogOrLabel: string
      noFavouritesTitle: string
      noFavouritesMessage: string
      noResultsTitle: string
      noResultsMessage: string
      searchAreaButton: string
    }
    SelectQuantityStep: {
      title: string
      expiryDateLabel: string
      titleOptimalVoucher: string
      totalLabel: string
      createOTPButton: string
      ticketAmountLabel: string
      automaticSelectionLabel: string
      noSolution: string
      calculateButton: string
      availableAmount: string
    }
    PaymentStep: {
      title: string
      payingLabel: string
      insertCodeLabel: string
      cancelButton: string
      otpValidityLabel: string
      minutes: string
      paymentSuccessTitle: string
      paymentSuccessMessage: string
      paymentErrorTitle: string
      paymentErrorMessage: string
      paymentFetchFailedMessage: string
      paymentFetchFailedTitle: string
      closeButton: string
    }
  }
}

export type CatalogI18n = {
  Catalog: {
    InformativeBanner: {
      Title: string
      Text: string
    }
    SuggestAPartnership: {
      Title: string
      Text: string
      ButtonLabel: string
    }
    CatalogCategories: {
      ButtonLabel: string
    }
    CatalogProductList: {
      Title: string
    }
    Showcase: {
      SearchPlaceholder: string
      SearchDiscountPlaceholder: string
      SearchEmptyMessage: string
      DataSetChanged: string
      CloseSearch: string
      Favourites: string
      Discount: string
      ErrorSearch: string
      Map: {
        showMapLabel: string
        hideMapLabel: string
        restorePositionLabel: string
        openProductLabel: string
        searchByLabel: string
        searchByAddress: string
        searchByProductName: string
        distance: string
      }
    }
    FavouritesBanner: {
      Favourites: {
        Title: string
        Text: string
        ButtonLabel: string
      }
      PosteXTe: {
        Title: string
        Text: string
        ButtonLabel: string
      }
    }
    Favourites: {
      BreadcrumbLabel: string
      SearchPlaceholder: string
      SearchEmptyMessage: string
      DataSetChanged: string
      CloseSearch: string
      EmptyMessage: string
      Title: string
      Description: string
    }
  }
}

export type Newsi18n = {
  News: {
    Title: string
    BackHome: string
    DownloadPdf: string
    DownloadPdfErrorMessage: string
    OpenDetail: string
    LoadMore: string
  }
}

export type UserInfoI18n = {
  UserInfo: {
    FamilyRelationships: {
      [key in Relationship]?: {
        label: string
        shortLabel: string
      }
    }
    UserPropertyForm: {
      InputPlaceholder: string
      Update: string
      Cancel: string
      ValidationMessages: {
        Required: {
          Email: string
          Iban: string
        }
        Format: {
          Email: string
          Iban: string
        }
      }
      Errors: {
        userIban: {
          GenericSaveError: string
          BadRequestError: string
        }
        userEmail: {
          GenericSaveError: string
          BadRequestError: string
        }
      }
    }
    UserDataCard: {
      Update: string
    }
    InformativeBannerSection: {
      Title: string
      Text: string
    }
    SimpleTextSection: {
      Text: string
    }
    FamilyMembersSection: {
      Title: string
      ButtonLabel: string
    }
    FamilyTable: {
      Errors: {
        DownloadPrivacyFileError: string
      }
      AriaLabels: {
        DownloadAction: string
      }
      DependentLabel: string
      PrivacyDocumentLabel: string
      NameAndSurname: string
      NameLabel: string
      SurnameLabel: string
      FiscalCodeLabel: string
      ParentageLabel: string
      NoFamilyMembersYet: string
      UpdateLabel: string
      DeleteLabel: string
      SuccessMessage: string
      ErrorMessage: string
      DeleteFamilyMemberTitle: string
      DeleteFamilyMemberMessage: string
      MemberAssociatedErrorMessage: string
    }
    FamilyForm: {
      ValidationMessages: {
        Custom: {
          FiscalCodeNotUnique: string
          FiscalCodeEqualsToRequestor: string
        }
        Required: {
          FirstName: string
          LastName: string
          FiscalCode: string
          Relationship: string
          TaxDependent: string
        }
        Max: {
          FirstName: string
          LastName: string
        }
        Min: {
          FirstName: string
          LastName: string
          FiscalCode: string
        }
        Format: {
          FirstName: string
          LastName: string
          FiscalCode: string
        }
      }
      SaveErrors: {
        NoDataModified: string
        BadRequest: string
        AddGenericError: string
        UpdateGenericError: string
        PrivacyFileUploadError: string
      }
      Header: {
        Add: string
        Update: string
        AddMessageText: string
        UpdateMessageText: string
      }
      Body: {
        Form: {
          InputTextPlaceholder: string
          DropdownPlaceholder: string
          NameLabel: string
          SurnameLabel: string
          FiscalCodeLabel: string
          ParentageLabel: string
          DependentLabel: string
          AwarenessConditionText: string
          AwarenessBullets: string[]
          ParentBullet: string
        }
        FileUploader: {
          Title: string
          Description: string
          TemplateButtonLabel: string
          SupportedFilesLabel: string
          SupportedSizeLabel: string
          UploadButtonLabel: string
          UploadAreaLabel: string
          DeleteButtonLabel: string
          ErrorFileSizeNotSupported: string
          ErrorFileTypeNotSupported: string
          ErrorFileLoading: string
          ErrorFileDownload: string
          PrivacyFileName: string
        }
        AwarenessCheckboxLabel: string
      }
      Footer: {
        Save: string
        Cancel: string
      }
    }
  }
}

export type UserAccountI18n = {
  UserAccountInformativeBannerSection: {
    Description: string
  }
  WalletOverview: {
    AvailableCreditText: string
    FringeBenefitWidget: {
      Title: string
      SpinnerText: string
    }
    UsedUntilNow: string
    AdditionalCreditUsedText: string
    AdditionalCreditSpendingReminderText: string
    Checkpoint: string
    CheckpointUnlockedAriaLabel: string
    CheckpointLockedAriaLabel: string
    PrizeDistribution: {
      totalBonusNonUtilizzato: string
      totalBonusUtilizzato: string
      totalPdrNonUtilizzato: string
      totalPdrUtilizzato: string
    }
  }
  FilterSection: {
    FilterBtnLabel: string
    StatusFilterLabel: string
    StatusFilterDropdownPlaceholder: string
    CampaignFilterLabel: string
    CampaignFilterDropdownPlaceholder: string
    DateFormatPlaceholder: string
    RangeLabel: string
    SortLabel: string
    RangeLabelFrom: string
    RangeLabelTo: string
    RangeLabelApplyButton: string
    FiltersClearLabelButton: string
    SortAsc: string
    SortDesc: string
    LastSevenDays: string
    LastMonth: string
    LastThreeMonths: string
    TabsLabel: string
  }
  Tabs: {
    Direct: string
    Refund: string
    MoneyTransfer: string
    Discount: string
  }
  Direct: {
    PriceLabel: string
    SeeDetailsLabel: string
  }
  Refund: {
    PriceLabel: string
    SeeDetailsLabel: string
  }
  MoneyTransfer: {
    PriceLabel: string
    SeeDetailsLabel: string
  }
  Discount: {
    PriceLabel: string
    SeeDetailsLabel: string
  }
  Prize: {
    Title: string
    PriceLabel: string
    SeeDetailsLabel: string
    ClaimPrizeLabel: string
    ClaimPrizeMsg: string
  }
}
export type ProductDetailI18n = {
  ProductDetail: {
    facePriceGratis: string
    typologies: {
      [key in companyagreementApi.Product['typology']]: {
        ButtonLabel: string
      }
    }
    discount: {
      percentage: string
      fixed: string
      generic: string
      available: string
    }
    multiPurchaseDescription: string
    multiPurchaseTotalDescription: string
    productVariantListDropDownTitle: string
    addToFavourite: string
    removeFromFavourite: string
    productVariantListDropDownDescription: string
    productVariantNameAriaLabel: string
    descriptionPlaceholder: string
    errorModal: {
      title: string
      btnOkLabel: string
      btnAnagraficaSectionLabel: string
      p1: string
      p2: string
      p3: string
      p4: string
      p5: string
      errorMultipurchase: string
    }
    productActivationModal: {
      okBtnLabel: string
      cancelBtnLabel: string
      beneficiarySelectionInfo: {
        all: string
        dependentChildrenAndSpouse: string
        employeeAndFamilyMemberDependent: string
        familyMember: string
        familyMemberDependent: string
      }
      headerNoFamilyMembers: {
        title: string
        btnAnagraficaSectionLabel: string
        p1: string
        p2: string
      }
      headerNotEmployee: {
        title: string
        btnAnagraficaSectionLabel: string
        p1: string
        p2: string
        p3: string
        p4: string
        p5: string
        p6: string
      }
      headerFinalUsage: {
        title1: string
        title2: string
        p: string
      }
      headerConfirmTitle: {
        title: string
      }
      showPersonas: {
        btnCancelAriaLabel: string
      }
      bodyFamilyMemberSelection: {
        singleMemberTitle: string
        multipleMemberTitle: string
        multiselectPlaceholder: string
        multiselectEmptyMessage: string
        dropdownPlaceholder: string
      }
      finalUsageSelection: {
        title: string
        dropdownPlaceholder: string
      }
      personalDataDiscalimer: {
        title: string
        description: string
      }
      privacyList: {
        title: string
        btnInformativaLabel: string
      }
    }
    productPartnership: {
      altPosteXTe: string
      goToPosteXTe: string
    }
    Errors: {
      addToFavourite: string
      removeFromFavourite: string
    }
  }
}

export type ShoppingI18n = {
  SafetyThresholdBox: {
    title: string
    message: string
    dontShowAgain: string
  }
  Direct: {
    Common: {
      pwsLogo: string
      balanceDescription: string
      freeAmount: string
    }
    GiftCard: {
      cardToActivate: {
        activateCardButtonLabel: string
        header: string
        trailer: string
        informativeBanner: string
        activationDescriptionFunnelMode: string
        activationDescriptionDetailsMode: string
        usageButtonLabel: string
      }
      cardActive: {
        mainHeader: string
        pinHeader: string
        getYourGiftCardButtonLabel: string
        informativeBanner: string
        informativeBannerFunnelMode: string
      }
      cancelOrder: {
        title: string
        body: string
        cancelButtonLabel: string
        cancelErrorMessage: {
          title: string
          subtitle: string
        }
        cancelSuccessMessage: {
          title: string
          subtitle: string
        }
      }
      takeInChargeMessage: string
      successMessage: string
      almostDoneTitle: string
      detailPageTitle: string
      getYourCodeButtonLabel: string
      requestInProgressTitle: string
      requestInProgressDescription: string
      loadingInProgressTitle: string
      loadingInProgressDescription: string
      productDescription: string
      priceDescription: string
      detailDescription: string
      buyerDescription: string
      usageDestinationDescription: string
      buyDateDescription: string
      expiringDateDescription: string
      noExpiringDateDescription: string
      confirmButtonLabel: string
      loadingInProgressErrorMessage: string
      requestInFailErrorMessage: string
      giftCardNotFoundMessage: string
      orderIdLabel: string
      confirmDialog: {
        title: string
        body: string
      }
    }
    Voucher: {
      priceDescription: string
      productDescription: string
      voucherNotFoundMessage: string
      loadingInProgressErrorMessage: string
      loadingInProgressDescription: string
      loadingInProgressTitle: string
      voucherCodeDescription: string
      buyerDescription: string
      buyDateDescription: string
      expiringDateDescription: string
      noExpiringDateDescription: string
      almostDoneTitle: string
      requestInProgressTitle: string
      orderIdLabel: string
      cancelOrder: {
        title: string
        body: string
        cancelButtonLabel: string
        cancelErrorMessage: {
          title: string
          subtitle: string
        }
        cancelSuccessMessage: {
          title: string
          subtitle: string
        }
      }
      confirmDialog: {
        title: string
        body: string
      }
      detailDescription: string
      confirmButtonLabel: string
      requestInProgressDescription: string
      detailPageTitle: string
      getYourCodeButtonLabel: string
      requestInFailErrorMessage: string
      takeInChargeMessage: string
      successMessage: string
      informativeBannerFunnelMode: string
      informativeBannerDetailsMode: string
    }
    MultiVoucher: {
      multivoucherSummaryTitle: string
      multivoucherSummaryQnt: string
      errorAllFailedMsg1: string
      errorAllFailedMsg2: string
      errorAnyFailedMsg1: string
      errorAnyFailedMsg2: string
      errorAnyFailedMsg3: string
      priceDescription: string
      productDescription: string
      voucherNotFoundMessage: string
      loadingInProgressErrorMessage: string
      loadingInProgressDescription: string
      loadingInProgressTitle: string
      voucherCodeDescription: string
      buyerDescription: string
      buyDateDescription: string
      expiringDateDescription: string
      noExpiringDateDescription: string
      almostDoneTitle: string
      requestInProgressTitle: string
      orderIdLabel: string
      confirmDialog: {
        title: string
        body: string
      }
      detailDescription: string
      confirmButtonLabel: string
      requestInProgressDescription: string
      detailPageTitle: string
      getYourCodeButtonLabel: string
      requestInFailErrorMessage: string
      takeInChargeMessage: string
      successMessageMsg1: string
      successMessageMsg2: string
      informativeBannerFunnelMode: string
      informativeBannerDetailsMode: string
      Usage: {
        Kinds: {
          [kindId: string]: {
            domiciliationOptionsPlaceholder: string
            DomiciliationOptions: {
              key: string
              text: string
            }[]
            SubCategories?: {
              [subCategoryId: string]: {
                contractIdLabel: string
                contractIdFieldName: string
                invoiceDisclaimer?: string
                Errors: {
                  Required: {
                    contractId: string
                  }
                }
              }
            }
            Errors: {
              Required: {
                invoiceId: string
                providerId?: string
                customerId: string
                invoiceDate: string
                domiciliation: string
                dueDate: string
                invoiceTotal: string
                requestedRefund: string
              }
              Custom: {
                domiciliationBlocking: string
                expired: string
                expiring: string
              }
              Min: {
                requestedRefundNumber: string
                invoiceTotalNumber: string
              }
              Max: {
                requestedRefundNumber: string
                invoiceDate: string
              }
              Matches: {
                requestedRefund: string
                invoiceTotal: string
              }
            }
          }
        }
        Errors: {
          RequestGenericError: string
          UnrecognizedKind: string
          Required: {
            customExternalKey: string
          }
          Max?: object
          Min?: object
          Matches: {
            customExternalKey: string
          }
          Custom: {
            customExternalKey: string
          }
        }
        Funnel: {
          wizardTitle: string
          invoiceUploadBtnLabel: string
          continueBtnLabel: string
          confirmBtnLabel: string
          abortRequestBtnLabel: string
          requestInProgressDescription: string
          goBackBtnLabel: string
          Dialogs: {
            Abort: {
              title: string
              body: string
            }
            Confirm: {
              title: string
              body: string
            }
            AdditionalDocuments: {
              title: string
              okButtonLabel: string
              requestButtonLabel: string
              requestButtonLabelPrefix: string
            }
          }
          Steps: {
            InvoiceUpload: {
              title: string
              descriptionPrefix: string
            }
            InvoiceUpdate: {
              title: string
              description: string
              descriptionPrefix: string
              FormFields: {
                Labels: {
                  documentId: string
                  documentDate: string
                  customerId: string
                  dueDate: string
                  Fiber: {
                    providerId: string
                  }
                  domiciliation: string
                  requestedRefund: string
                  invoiceTotal: string
                  placeholder: string
                  dateFormatPlaceholder: string
                  dateFormat: string
                  formTrailer: string
                }
              }
            }
            AdditionalDocumentsUpload: {
              title: string
            }
            VoucherSelection: {
              title: string
              description: string
              yourVoucherDescription: string
              voucherCounterTitle: string
              availableVouchers: string
              usedVouchers: string
              increaseVoucherAriaLabel: string
              decreaseVoucherAriaLabel: string
              amountToPayLabel: string
              voucherCounterOverviewTable: string
              totalLabel: string
            }
            ConfirmationStep: {
              title: string
              productDescription: string
              priceDescription: string
              balanceDescription: string
              DetailBox: {
                title: string
                beneficiaryNameSurname: string
                requestDate: string
                beneficiaryFiscalCode: string
                requestedRefund: string
                voucherAmount: string
                invoiceId: string
                documentDate: string
                vendorName: string
                vendorTaxId: string
                invoiceTotal: string
                privacyStatement: string
              }
            }
            SuccessStep: {
              trailer: string
              footer: string
            }
          }
        }
        Detail: {
          loadingInProgressTitle: string
          loadingInProgressDescription: string
          notFoundMessage: string
          loadingInProgressErrorMessage: string
          invoiceDescription: string
          title: string
          buyerDescription: string
          priceDescription: string
          koOutcomeMessage: string
          koOutcomeWithReasonMessage: string
          detailDateDescription: string
          documentNumberDescription: string
          documentDateDescription: string
          documentExpirationDescription: string
          documentAmountDescription: string
        }
        Request: {
          loadingInProgressTitle: string
          loadingInProgressDescription: string
          notFoundMessage: string
          loadingInProgressErrorMessage: string
        }
      }
    }
    GoodAndService: {
      priceDescription: string
      productDescription: string
      voucherNotFoundMessage: string
      almostDoneTitle: string
      orderIdLabel: string
      requestInProgressTitle: string
      cancelOrder: {
        title: string
        body: string
        cancelButtonLabel: string
        cancelErrorMessage: {
          title: string
          subtitle: string
        }
        cancelSuccessMessage: {
          title: string
          subtitle: string
        }
      }
      confirmDialog: {
        title: string
        body: string
      }
      loadingInProgressErrorMessage: string
      loadingInProgressDescription: string
      loadingInProgressTitle: string
      voucherCodeDescription: string
      buyerDescription: string
      buyDateDescription: string
      detailDescription: string
      confirmButtonLabel: string
      requestInProgressDescription: string
      detailPageTitle: string
      detailPageTitleGratis: string
      detailExternalPurchaseTitleDesc: string
      detailExternalPurchaseTitleId: string
      getYourCodeButtonLabel: string
      takeInChargeMessage: string
      requestInFailErrorMessage: string
      informativeBannerDetailsMode: string
      merchantLoginP1: string
      merchantLoginP2: string
      merchantLoginP3: string
      merchantLoginP4: string
      merchantLoginBtnLabel: string
      personalDataDiscalimer: {
        title: string
        description: string
      }
      productActivationModal: {
        okBtnLabel: string
        cancelBtnLabel: string
        title: string
      }
      errorMerchantLogin: string
    }
    StateMapping: {
      [stateKey: string]: {
        label: string
      }
    }
    Discount: {
      freeAmount: string
      discountToActivate: {
        activateDiscountButtonLabel: string
        header: string
        usageButtonLabel: string
        trailer: string
      }
      detailExternalPurchaseTitleDesc: string
      voucherCodeDescription: string
      discountActive: {
        mainHeader: string
        pinHeader: string
        getYourDiscountButtonLabel: string
      }
      merchantLoginP1: string
      merchantLoginP2: string
      merchantLoginP3: string
      merchantLoginP4: string
      merchantLoginBtnLabel: string
      personalDataDiscalimer: {
        title: string
        description: string
      }
      productActivationModal: {
        okBtnLabel: string
        cancelBtnLabel: string
        title: string
      }
      errorMerchantLogin: string
      takeInChargeMessage: string
      successMessage: string
      almostDoneTitle: string
      detailPageTitle: string
      getYourCodeButtonLabel: string
      requestInProgressTitle: string
      requestInProgressDescription: string
      loadingInProgressTitle: string
      productDescription: string
      priceDescription: string
      detailDescription: string
      buyerDescription: string
      getYourDiscountButtonLabel: string

      variantName: string
      buyDateDescription: string
      expiringDateDescription: string
      noExpiringDateDescription: string
      confirmButtonLabel: string
      loadingInProgressErrorMessage: string
      requestInFailErrorMessage: string
      discountNotFoundMessage: string
      orderIdLabel: string
      confirmDialog: {
        title: string
        body: string
      }
    }
  }
  Refund: {
    StateMapping: {
      [stateKey: string]: {
        label: string
      }
    }

    loadingInProgressTitle: string
    loadingInProgressDescription: string
    refundNotFoundMessage: string
    refundDetailTitle: string
    loadingInProgressErrorMessage: string
    priceDescription: string
    productDescription: string
    balanceDescription: string
    Errors: {
      RequestGenericError: string
      Uploader: {
        uploadGenericError: string
        deletingGenericError: string
        uploadDocumentsButtonLabel: string
        uploadMaxSizeError: string
        uploadFileTypeError: string
        fileTableTitle: string
        deleteFileLabel: string
        retryFileLabel: string
        supportedFileTypeAndMaxSize: string
        documentAiTimeOutMessage: string
        documentAiAnalyzeFailedMessage: string
      }
      Required: {
        invoiceId: string
        invoiceDate: string
        vendorName: string
        vendorTaxId: string
        invoiceTotal: string
        requestedRefund: string
        welfareCredit: string
        customExternalKey: string
      }
      Max: {
        invoiceDate: string
        requestedRefund: string
      }
      Min: {
        invoiceTotal: string
        requestedRefund: string
        welfareCredit: string
      }
      Matches: {
        invoiceTotal: string
        requestedRefund: string
        customExternalKey: string
        fiscalCode: string
      }
      Custom: {
        customExternalKey: string
      }
    }
    Detail: {
      invoiceDescription: string
      additionalDocumentsDescription: string
      orderIdLabel: string
      buyerDescription: string
      detailDateDescription: string
      documentNumberDescription: string
      documentDateDescription: string
      vendorNameDescription: string
      vendorTaxIdDescription: string
      documentAmountDescription: string
      koOutcomeMessage: string
      koOutcomeWithReasonMessage: string
      canceledOutcomeMessage: string
      cancelOrder: {
        title: string
        body: string
        cancelButtonLabel: string
        cancelErrorMessage: {
          title: string
          subtitle: string
        }
        cancelSuccessMessage: {
          title: string
          subtitle: string
        }
      }
    }
    Funnel: {
      wizardTitle: string
      invoiceUploadBtnLabel: string
      continueBtnLabel: string
      confirmBtnLabel: string
      abortRequestBtnLabel: string
      requestInProgressDescription: string
      goBackBtnLabel: string
      Dialogs: {
        Abort: {
          title: string
          body: string
        }
        Confirm: {
          title: string
          body: string
        }
        AdditionalDocuments: {
          title: string
          okButtonLabel: string
          requestButtonLabel: string
          requestButtonLabelPrefix: string
        }
      }
      Steps: {
        InvoiceUpload: {
          title: string
          descriptionPrefix: string
        }
        InvoiceUpdate: {
          title: string
          description: string
          descriptionPrefix: string
          FormFields: {
            Labels: {
              documentId: string
              documentDate: string
              vendorName: string
              vendorTaxId: string
              requestedRefund: string
              invoiceTotal: string
              placeholder: string
              dateFormatPlaceholder: string
              dateFormat: string
              formTrailer: string
            }
          }
        }
        AdditionalDocumentsUpload: {
          title: string
        }
        ConfirmationStep: {
          title: string
          DetailBox: {
            title: string
            beneficiary: string
            invoiceId: string
            documentDate: string
            vendorName: string
            customerTaxId: string
            vendorTaxId: string
            invoiceTotal: string
            privacyStatement: string
          }
        }
        SuccessStep: {
          trailer: string
          footer: string
        }
      }
    }
  }
  Transfer: {
    StateMapping: {
      [stateKey: string]: {
        label: string
      }
    }
    abortRequestBtnLabel: string
    goBackBtnLabel: string
    errorConfirmTitle: string
    Detail: {
      title: string
      labelImport: string
      detailBenecifiaryLabel: string
      detailDate: string
      orderIdLabel: string
      errorTitle: string
      errorDescription: string
    }
    loadingInProgressTitle: string
    loadingInProgressDescription: string
    loadingInProgressErrorMessage: string
    FondoPensione: {
      Errors: {
        ERR_Required: string
        ERR_SaldoRequired: string
        ERR_CreditoInsufficiente: string
        ERR_Importo: string
        ERR_Positive: string
      }
      WizardFristStep: {
        labelStep: string
        title: string
        subtitle: string
        labelProduct: string
        labelImport: string
        creditAvailableLabel: string
        btnLabel: string
      }
      WizardSecondStep: {
        labelStep: string
        title: string
        labelProduct: string
        labelImport: string
        creditAvailableLabel: string
        detailTitle: string
        detailBenecifiaryLabel: string
        btnConfirmLabel: string
        btnCancelLabel: string
        modalConfirmTitle: string
        modalConfirmBtnConfirmLabel: string
        modalConfirmBtnCancelLabel: string
        modalCancelTitle: string
        modalCancelBtnConfirmLabel: string
        modalCancelBtnCancelLabel: string
      }
      WizardFinalStepSubmitted: {
        title: string
        subtitle: string
        description: string
      }
      WizardErrorGeneric: {
        title: string
        btnRetryLabel: string
        btnGoToCatalogLabel: string
      }
      cancelOrder: {
        title: string
        body: string
        cancelButtonLabel: string
        cancelErrorMessage: {
          title: string
          subtitle: string
        }
        cancelSuccessMessage: {
          title: string
          subtitle: string
        }
      }
    }
    FondoSalute: {
      WizardFirstStepWithoutFamilyMembers: {
        title: string
        labelProduct: string
        labelImport: string
        creditAvailableLabel: string
        detailTitle: string
        detailBenecifiaryLabel: string
        btnConfirmLabel: string
        btnCancelLabel: string
        modalConfirmTitle: string
        modalConfirmBtnConfirmLabel: string
        modalConfirmBtnCancelLabel: string
        modalCancelTitle: string
        modalCancelBtnConfirmLabel: string
        modalCancelBtnCancelLabel: string
      }
      WizardFirstStepWithFamilyMembers: {
        labelStep: string
        title: string
        subtitle: string
        btnContinueLabel: string
      }
      WizardSecondStepWithFamilyMembers: {
        labelStep: string
        title: string
        labelProduct: string
        labelImport: string
        creditAvailableLabel: string
        detailTitle: string
        detailBenecifiaryLabel: string
        btnConfirmLabel: string
        btnCancelLabel: string
        modalConfirmTitle: string
        modalConfirmBtnConfirmLabel: string
        modalConfirmBtnCancelLabel: string
        modalCancelTitle: string
        modalCancelBtnConfirmLabel: string
        modalCancelBtnCancelLabel: string
      }
      WizardFinalStepSubmitted: {
        title: string
        subtitle: string
        description: string
      }
      WizardErrorGeneric: {
        title: string
        btnRetryLabel: string
        btnGoToCatalogLabel: string
      }
      cancelOrder: {
        title: string
        body: string
        cancelButtonLabel: string
        cancelErrorMessage: {
          title: string
          subtitle: string
        }
        cancelSuccessMessage: {
          title: string
          subtitle: string
        }
      }
      beneficiaryTitle: string
      beneficiaryName: string
      beneficiaryBirthDate: string
      warnBeneficiaryWrongAge: string
      warnBeneficiaryMaxAge: string
    }
  }
}

export type TutorialI18n = {
  Tutorial: {
    InformativeSection: {
      Title: string
      Text: string
    }
    MoreInfoSection: {
      Title: string
      Text: string
    }
    HowToSection: {
      Title: string
      Text: string
    }
    VideoSection: {
      Title: string
      Text: string
    }
  }
}

export type JoinFunnelI18n = {
  JoinFunnel: {
    Common: {
      notAvailableText: string
      notAvailableDescriptionText: string
      titleText: string
    }
    FirstStep: {
      title: string
      grossCard: {
        text: string
        moreInfoText: string
      }
      convertCard: {
        textBeforeFigure: string
        textAfterFigure: string
        netAmountConvertedIntoWelfare: string
        buttonText: string
      }
      doNotConvertCard: {
        text: string
        textBeforeFigure: string
        buttonText: string
      }
      modifyPercentageText: string
      nextBtnLabel: string
      choosePercentageDialog: {
        title: string
        text: string
        estimateText: string
        percentageDropdownText: string
        percentageDropdownPlaceholder: string
        additionalCreditText: string
        convertTitle: string
        convertEstimateText: string
        convertCard: {
          text: string
        }
        doNotConvertCard: {
          text: string
        }
        abortBtnLabel: string
        confirmBtnLabel: string
        modifyPercentageDialog: {
          title: string
          text: string
        }
      }
    }
    MoreDetailsDialog: {
      title: string
      text: string
      okBtnLabel: string
    }

    Confirm: {
      title: string
      convertSection: {
        textBeforeFigure: string
        textAfterFigure: string
        percentageDescription: string
        modifyPercentageBtnLabel: string
      }
      additionalCreditSection: {
        textBeforeFigure: string
        textAfterFigure: string
      }
      conversionSummaryBannerText: string
      conversionSummaryBannerTextDetails: string
      rulesAndPrivacyMessage: string
      joinRulesCheckboxes: string[]
      bannerStepsTitle: string
      joinBtnLabel: string
      documentsSection: {
        title: string
      }
      cancelJoinSection: {
        text: string
        btnLabel: string
        Errors: {
          GenericSaveError: string
        }
        unjoinConfirmationDialog: {
          title: string
          text: string
        }
        unjoinSuccess: {
          trailer: string
          footer: string
          goToLandingBtnLabel: string
        }
      }
      Errors: {
        GenericSaveError: string
      }
      goBackBtnLabel: string
    }
    Success: {
      trailer: string
      footer: string
    }
    Error: {
      text: string
    }
  }
}

export type ProposalsI18n = {
  Proposal: {
    SuggestSection: {
      Title: string
      Text: string
    }
    HowToSection: {
      Text: string
      HealthText: string
    }
    InfoEmailSection: {
      Text: string
    }
    CategoriesSection: {
      SuggestCardLabel: string
      SearchLabel: string
      SearchLabelSelected: string
      InputLabel: string
      InputPlaceholder: string
      ButtonLabel: string
      ModalConfirmBtnConfirmLabel: string
      ModalConfirmBtnCancelLabel: string
      ModalConfirmTitle: string
      ValidationErrorCategory: string
      ValidationErrorCompanyName: string
    }
    ProposalsFinalStepSubmitted: {
      Title: string
      Subtitle: string
      Description: string
    }
    ProposalsErrorGeneric: {
      Title: string
      BtnRetryLabel: string
      BtnGoToCatalogLabel: string
    }
    InstructionLinkSection: {
      Title: string
    }
  }
}

export type SelfRegistrationI18n = {
  SelfRegistration: {
    Title: string
    CloseBtn: string
    RegisterBtn: string
    Messages: {
      SummarySuccess: string
      DetailSuccess: string
      SummaryError: string
      DuplicatedUserError: string
      GenericError: string
    }
    Errors: {
      Required: string
      Invalid: string
      DuplicateEmployeeId: string
      ExistingEmployeeId: string
      DuplicateTaxCode: string
      ExistingTaxCode: string
      DuplicateCorporateEmail: string
      ExistingCorporateEmail: string
      InvalidEmail: string
      InvalidPhoneNumber: string
      EmployeeIdError: string
      ResidenceAddressInvalid: string
      TaxCodeInvalid: string
      NameInvalid: string
      CapInvalid: string
      IbanInvalid: string
      TaxCodeDataMismatch: string
      MaxLengthField: string
    }
    RegistryForm: {
      CompanyData: {
        Title: string
        EmployeeId: string
        CorporateEmail: string
      }
      RegistryData: {
        Title: string
        FirstName: string
        LastName: string

        Gender: {
          Label: string
          Data: {
            M: string
            F: string
          }
        }
        BirthDate: {
          Label: string
          Format: string
        }
        TaxCode: string

        PhoneNumber: string
        BirthCountry: string
        BirthProvince: string
        BirthMunicipality: string
      }
      TermsAndConditions: {
        Title: string
        PrivacyText: string
        PrivacyRequiredText: string
        PrivacyDocumentRequiredText: string

        IdentityText: string
        IdentityRequiredText: string
        DocumentPrivacyLabel: string
      }
    }
  }
}

export type DemoAppI18n = {
  AdminPanelTitle: string
  AdminPanelDescription: string
  CreditLabel: string
  UserInfoLabel: string
  FringeCreditLabel: string
  HideDemoLabel: string
  NoFringeCreditLabel: string
  ConfirmButtonLabel: string
  ResetDataLabel: string
  ResetDataText: string
  ResetDataButtonLabel: string
  FirstNameLabel: string
  LastNameLabel: string
  EmailLabel: string
  CampaignConfigurationLabel: string
  LayoutConfigurationLabel: string
  SimulateExternalOrderLabel: string
  SimulateExternalOrderCompletionLabel: string
  InformativeBannerDemoAppTitle: string
  CloseButtonLabel: string
  InformativeBannerDemoApp: string
  ButtonLabel: string
}

export type I18nResources = {
  translation: unknown // default namespace is untyped to force you to create a type for each namespace and specify it in useTranslation hook
  home: HomeI18n
  landing: LandingI18n
  catalog: CatalogI18n
  news: Newsi18n
  errors: ErrorsI18n
  userInfo: UserInfoI18n
  userAccount: UserAccountI18n
  productDetail: ProductDetailI18n
  shopping: ShoppingI18n
  tutorial: TutorialI18n
  joinFunnel: JoinFunnelI18n
  proposal: ProposalsI18n
  selfRegistration: SelfRegistrationI18n
  mealVouchers: MealVouchersI18n
  demoApp: DemoAppI18n
}

export type I18nData = {
  resources: Record<string, I18nResources>
  namespaces: string[]
}

export type UserInfo = {
  name: string
  balance: number
}

export type CampaignPhase = membershipApi.CompanyWelfareCampaign['phase']

export type CampaignInfo = {
  phase: CampaignPhase
  deadline?: string
}

export type BaseFileType = {
  documentId?: string
  blob?: Blob
  documentUri?: string
  documentName: string
  documentSize?: number
  documentMimeType?: string
}

export type FileType = {
  serverStatus?: PrivacyDocument.StatusEnum
} & BaseFileType

export enum FileProcessingStatusType {
  CREATED = 0,
  DOCUMENTID_ASSIGNED = 1,
  UPLOADED = 2,
  UPLOAD_NOTIFIED = 3,
  PROCESSED = 4,
  PROCESSED_TIMEOUT = 5,
}

export type DocumentFileType = {
  documentType: string
  status: FileProcessingStatusType
  errorOccurred?: boolean
  documentDetails?: documentAiApi.Document
  analyzeOperationStatus?: documentAiApi.OperationStatus
  analyzeOperationId?: string
} & BaseFileType

export type FileUploadPropsType = {
  title: string
  description: string
  templateBtnLabel: string
  templateBtnFileLink?: string
  templateBtnFileName?: string
  acceptExtensions: string
  acceptTypes: string
  maxUploadFileSize: number
  supportedFilesLabel: string
  supportedSizeLabel: string
  uploadButtonLabel: string
  uploadAreaLabel: string
  deleteButtonLabel: string
  errorFileTypeNotSupported: string
  errorFileSizeNotSupported: string
  errorFileLoading: string
  errorFileDownload: string
  file?: FileType
  templateUri?: string
  onFileStateChange?: (file?: FileType) => void
  serverPrivacyFileExists?: boolean
  downloadDocument?: () => void
}

export type PersonalDataComponentPropsType = {
  allowFamilyMemberDelete?: boolean
  allowFamilyMemberCFUpdate?: boolean
  maxUploadFileSize?: number
}

export type FamilyFormPropsType = {
  callbackOnFamilyFormHide: () => void
  isEdit?: boolean
  familyMember?: UserFamilyMember
  membership: membershipApi.MembershipExtended
} & PersonalDataComponentPropsType

export const DefaultCFRegex = /^[A-Z]{6}[0-9]{2}[A-EHLMPR-T][0-9]{2}[A-Z0-9]{4}[A-Z]$/gm

export type ServiceCategory = 'welfareArt51' | 'welfareArt100' | 'none' | undefined

export interface GenericOrderItem {
  orderDate: string
  productTypology: string
  status: string
  productName: string
  priceLabel: string | undefined
  discountValue?: string | undefined
  discountType?: string | undefined
  price?: number
  seeDetailsLabel: string
  detailsLink?: string
}
export interface OrderListFilters {
  sort?: string
  status?: string[]
  from?: string
  to?: string
  offset?: number
  limit?: number
  membershipId?: string
}

export interface IconStyle {
  stroke?: string
  width: number
  height?: number
  fill: string
  className?: string
}
export interface IconsCheckpoint {
  checkpoint: number
  icon: (par: IconStyle) => React.ReactNode
}

export type MultiVoucherGroupedType = {
  value: number
  availableQuantity: number
}
export type MultiVoucherSelectedType = {
  value: number
  selectedQuantity: number
}

export type SubscriptionSimulationInfoType = {
  premioConvertito: number
  premioNonConvertito: number
  totaleNetto: number
  conversioneInCredito: number
  integrazionePoste: number
  importoBustaPaga: number
}

export type JoinPercentageOption = {
  key: string
  value: number
}

/* MAP */
export type PositionType = {
  lat: number
  lon: number
}
export type AddressResultType = {
  position: PositionType
  id?: string
  score?: number
  address?: { freeformAddress: string }
  radius?: number
  boundingBox?: atlas.data.BoundingBox
}

export type GeoMapEventType = {
  evt: atlas.MapEvent
  locationRadius: number
  zoom?: number
}

export type AtlasConfigurationType = {
  enableMap: boolean
  useGeolocalization: boolean
  maxLocationRadius?: number
  defaultPosition?: PositionType
  authenticationMethod: atlas.AuthenticationType
  subscriptionKey?: string
  aadTenant?: string
  aadInstance?: string
  aadAppId?: string
  showMapCircle: boolean
}
export type MapPropsType = {
  address?: AddressResultType
  defaultPosition?: atlas.data.Position
  positionRestoreCallBack?: () => void
  authOptions?: atlas.AuthenticationOptions
  productGridList: companyagreementApi.ProductListItem[]
  onProductClick: (product: companyagreementApi.ProductListItem) => void
  onMapEvent?: (geoMapEvent: GeoMapEventType) => void

  mapConfig?: portalConfigurationApi.MapsConfig
}

export enum MapSearchMode {
  ADDRESS = 0,
  PRODUCTNAME = 1,
}

export type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>
}

export interface RegistryItem {
  id: string // used for internal purposes only
  companyCode: string // CodAzienda
  employeeId: string // Matricola
  corporateEmail: string // EmailAziendale
  taxCode: string // CodiceFiscale
  lastName: string // Cognome
  firstName: string // Nome
  gender: string // Sesso
  birthDate: string // DataNascita
  privacyAccepted?: boolean // Privacy consent
  healthDataProcessingAccepted?: boolean // Health data processing consent
  responsibilityDeclarationAccepted?: boolean // Responsibility declaration for provided information
  privacyDocumentRead?: boolean // Privacy document has been read
}

export interface RegistryItemDropdownInputProps extends DropdownProps {
  options: unknown[]
  optionLabel?: string
  value?: unknown
  labelProps: {
    text?: string
    labelStyle?: CSSProperties
  }
}

export interface RegistryItemTextInputProps extends InputTextProps {
  labelProps: {
    text?: string
    labelStyle?: CSSProperties
  }
}

export interface RegistryInputNumberProps extends InputNumberProps {
  labelProps: {
    text?: string
    labelStyle?: CSSProperties
  }
}

export type RegistryCalendarProps = CalendarProps & {
  labelProps: {
    text?: string
    labelStyle?: CSSProperties
  }
}

export interface FormAutocompleteDropdownProps extends AutoCompleteProps {
  options: unknown[]
  optionLabel?: string
  value?: unknown
  labelProps: {
    text?: string
    labelStyle?: CSSProperties
    testId?: string
  }
}

export type VendorHours = {
  openNow: boolean
  openingHoursText: string
}

export type GeoLocation = {
  latitude: number
  longitude: number
}

export type VendorMapPropsType = {
  address?: AddressResultType
  defaultPosition?: atlas.data.Position
  positionRestoreCallBack?: () => void
  authOptions?: atlas.AuthenticationOptions
  vendorsList: mealVouchersApi.UnitMerchant[]
  onVendorClick: (vendor: mealVouchersApi.UnitMerchant | undefined) => void
  onMapEvent?: (geoMapEvent: GeoMapEventType) => void
  onPositionChange?: (position: PositionType) => void
  onZoomChange?: (zoom: number | undefined) => void

  mapConfig?: {
    atlasInitialFreeZoom: number
    atlasInitialAddressZoom: number
  }
}

export type MealVoucher = {
  assetCode: string
  quantity: number
  amount: number
  expiryDate: string
  selectedQuantity: number
}

export type OrderDetailsExtended = OrderDetails & {
  orderDate: string
  detailPageUrl: string
  externalPageUrl?: string
  serviceCategory?: ServiceCategory
}
